import * as React from "react";
import { Card, CardContent } from "./card";
import { Lock, Twitter, Linkedin, Instagram, Youtube } from "lucide-react";
import { MetricsGrid } from "./components/features/financial/metrics-grid";
import { ChartStackedBar } from "./components/features/financial/chart-stacked-bar";
import { ChartDonut } from "./components/features/financial/chart-donut";
import { FinancialTable } from "./components/features/financial/financial-table";
import { EmailList } from "./components/features/financial/email-list";
import { LogoGrid } from "./components/features/financial/logo-grid";
import { ComparisonTable } from "./components/features/financial/comparison-table";
import { ProcessFlow } from "./components/features/financial/process-flow";
import { FeatureList } from "./components/features/financial/feature-list";
import { Timeline } from "./components/features/financial/timeline";
import { HighlightBox } from "./components/features/financial/highlight-box";

// --- Types ---
export type BlockType = 'text' | 'metrics' | 'team' | 'features' | 'key_value' | 'alert' | 'button' | 'metrics_display' | 'chart_stacked_bar' | 'chart_donut' | 'financial_table' | 'layout_split' | 'email_list' | 'metrics_grid' | 'logo_grid' | 'comparison_table' | 'process_flow' | 'feature_list' | 'timeline' | 'highlight_box';

export interface Block {
  id: string;
  type: BlockType;
  data: any;
}

// --- Block Components ---

export const TextBlock = ({ data }: { data: { html?: string, markdown?: string, className?: string } }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // If we have HTML content, scan for script tags and execute them.
    // This is required to support user-provided (admin) code snippets like widgets or trackers.
    if (data.html && containerRef.current) {
        const container = containerRef.current;
        const scripts = container.querySelectorAll('script');

        scripts.forEach((oldScript) => {
            const newScript = document.createElement('script');
            
            // Copy all attributes
            Array.from(oldScript.attributes).forEach((attr) => {
                newScript.setAttribute(attr.name, attr.value);
            });

            // Copy script content
            if (oldScript.innerHTML) {
                newScript.appendChild(document.createTextNode(oldScript.innerHTML));
            }

            // Replace the old script with the new executable one
            if (oldScript.parentNode) {
                oldScript.parentNode.replaceChild(newScript, oldScript);
            }
        });
    }
  }, [data.html]);

  if (data.html) {
      return <div ref={containerRef} className={`prose prose-neutral max-w-none ${data.className || ''}`} dangerouslySetInnerHTML={{ __html: data.html }} />;
  }
  return <div className={`prose prose-neutral max-w-none ${data.className || ''}`}>{data.markdown}</div>;
};

// Updated to use the shared MetricsGrid component for consistent styling
export const MetricsBlock = ({ data }: { data: { metrics: Array<{ label: string, value: string, subtext?: string, subtextClass?: string }> } }) => {
  return <MetricsGrid data={data} />;
};

export const FeaturesBlock = ({ data }: { data: { items: Array<{ title: string, description?: string, list?: string[] }> } }) => {
    if (!data?.items || !Array.isArray(data.items)) return null;
    return (
        <div className="grid gap-6 my-8">
            {data.items.map((item: any, idx: number) => (
                <Card key={idx}>
                    <CardContent className="p-6">
                        <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                        {item.description && <p className="text-neutral-500 mb-4">{item.description}</p>}
                        {item.list && (
                            <ul className="list-disc list-inside text-neutral-500 space-y-1">
                                {item.list.map((li: string, liIdx: number) => <li key={liIdx}>{li}</li>)}
                            </ul>
                        )}
                    </CardContent>
                </Card>
            ))}
        </div>
    );
};

export const KeyValueBlock = ({ data }: { data: { title?: string, items: Array<{ label: string, value: string }> } }) => {
    if (!data?.items || !Array.isArray(data.items)) return null;
    return (
        <Card className="my-6">
            <CardContent className="p-6">
                {data.title && <h3 className="text-xl font-semibold mb-4">{data.title}</h3>}
                <div className="space-y-4">
                    {data.items.map((item: any, idx: number) => (
                        <div key={idx} className="flex justify-between items-center border-b border-neutral-100 pb-2 last:border-0 last:pb-0">
                            <span className="font-medium">{item.label}</span>
                            <span className="text-neutral-600">{item.value}</span>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

export const TeamBlock = ({ data }: { 
    data: { 
        variant?: 'featured' | 'grid',
        members: Array<{ 
            name: string, 
            role: string, 
            bio: string, 
            imageUrl?: string,
            socials?: {
                twitter?: string,
                linkedin?: string,
                instagram?: string,
                youtube?: string
            }
        }> 
    } 
}) => {
    if (!data?.members || !Array.isArray(data.members)) return null;
    const isFeatured = data.variant === 'featured';
    
    // Featured Layout (Full Width Row)
    if (isFeatured) {
        return (
            <div className="space-y-4 my-6">
                {data.members.map((member: any, idx: number) => (
                    <Card key={idx} className="overflow-hidden border-neutral-200 bg-white hover:border-neutral-300 transition-colors">
                        <CardContent className="p-5">
                            <div className="flex flex-col sm:flex-row gap-5 items-start">
                                {/* Avatar */}
                                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-neutral-100 rounded-xl flex-shrink-0 overflow-hidden relative ring-1 ring-neutral-200">
                                     {member.imageUrl ? (
                                         <img src={member.imageUrl} alt={member.name} className="object-cover w-full h-full" />
                                     ) : (
                                         <div className="flex items-center justify-center w-full h-full text-neutral-300 font-bold text-xl">
                                             {member.name.charAt(0)}
                                         </div>
                                     )}
                                </div>
                                
                                {/* Content */}
                                <div className="flex-1 min-w-0 pt-0.5">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <h3 className="font-semibold text-lg text-neutral-900">{member.name}</h3>
                                            <div className="text-xs font-medium text-neutral-600 bg-neutral-100 border border-neutral-200 px-2 py-0.5 rounded-md">
                                                {member.role}
                                            </div>
                                        </div>
                                        
                                        {/* Socials */}
                                        {member.socials && (
                                            <div className="flex gap-1.5">
                                                {member.socials.twitter && (
                                                    <a href={member.socials.twitter} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md text-neutral-400 hover:text-black hover:bg-neutral-50 transition-all">
                                                        <Twitter className="w-3.5 h-3.5" />
                                                    </a>
                                                )}
                                                {member.socials.linkedin && (
                                                    <a href={member.socials.linkedin} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md text-neutral-400 hover:text-blue-600 hover:bg-neutral-50 transition-all">
                                                        <Linkedin className="w-3.5 h-3.5" />
                                                    </a>
                                                )}
                                                {member.socials.instagram && (
                                                    <a href={member.socials.instagram} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md text-neutral-400 hover:text-pink-600 hover:bg-neutral-50 transition-all">
                                                        <Instagram className="w-3.5 h-3.5" />
                                                    </a>
                                                )}
                                                {member.socials.youtube && (
                                                    <a href={member.socials.youtube} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md text-neutral-400 hover:text-red-600 hover:bg-neutral-50 transition-all">
                                                        <Youtube className="w-3.5 h-3.5" />
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <p className="text-neutral-600 text-sm leading-relaxed">{member.bio}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        );
    }

    // Standard Grid Layout
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            {data.members.map((member: any, idx: number) => (
                <Card key={idx} className="group hover:shadow-md transition-all duration-300">
                    <CardContent className="p-6">
                        <div className="flex gap-4 items-start mb-4">
                            <div className="w-14 h-14 bg-neutral-200 rounded-full flex-shrink-0 overflow-hidden relative ring-2 ring-neutral-100 group-hover:ring-neutral-200 transition-all">
                                 {member.imageUrl ? (
                                     <img src={member.imageUrl} alt={member.name} className="object-cover w-full h-full" />
                                 ) : (
                                     <div className="flex items-center justify-center w-full h-full text-neutral-400 font-bold text-xl">
                                         {member.name.charAt(0)}
                                     </div>
                                 )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-lg text-neutral-900 truncate">{member.name}</h3>
                                <div className="text-sm text-neutral-500 font-medium mb-1">{member.role}</div>
                            </div>
                        </div>
                        
                        <p className="text-sm text-neutral-600 line-clamp-3 mb-4">{member.bio}</p>
                        
                        {/* Socials for Grid */}
                        {member.socials && (
                            <div className="flex gap-2 pt-2 border-t border-neutral-100">
                                {member.socials.twitter && (
                                    <a href={member.socials.twitter} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md text-neutral-400 hover:text-black hover:bg-neutral-50 transition-colors">
                                        <Twitter className="w-4 h-4" />
                                    </a>
                                )}
                                {member.socials.linkedin && (
                                    <a href={member.socials.linkedin} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md text-neutral-400 hover:text-blue-600 hover:bg-neutral-50 transition-colors">
                                        <Linkedin className="w-4 h-4" />
                                    </a>
                                )}
                                {member.socials.instagram && (
                                    <a href={member.socials.instagram} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md text-neutral-400 hover:text-pink-600 hover:bg-neutral-50 transition-colors">
                                        <Instagram className="w-4 h-4" />
                                    </a>
                                )}
                                {member.socials.youtube && (
                                    <a href={member.socials.youtube} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md text-neutral-400 hover:text-red-600 hover:bg-neutral-50 transition-colors">
                                        <Youtube className="w-4 h-4" />
                                    </a>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            ))}
        </div>
    );
};

export const AlertBlock = ({ data }: { data: { type?: 'info' | 'warning' | 'lock', message: string } }) => {
    const isLock = data.type === 'lock';
    return (
        <div className={`p-4 rounded-md flex gap-3 items-start my-6 ${isLock ? 'bg-neutral-100 text-neutral-600' : 'bg-blue-50 text-blue-800'}`}>
            {isLock && <Lock className="w-5 h-5 flex-shrink-0 mt-0.5" />}
            <p className="text-sm">{data.message}</p>
        </div>
    );
};

export const ButtonBlock = ({ data }: { data: { label: string, url?: string, action?: string, style?: 'primary' | 'outline' } }) => {
    const baseStyles = "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-200 disabled:opacity-50 disabled:pointer-events-none h-10 px-4 py-2 text-sm no-underline";
    const variantStyles = data.style === 'outline'
      ? "border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-900"
      : "bg-neutral-900 text-white hover:bg-neutral-800 border border-transparent";
    
    if (data.url) {
        return (
            <div className="my-6">
                 <a href={data.url} className={`${baseStyles} ${variantStyles}`}>
                    {data.label}
                 </a>
            </div>
        )
    }
    return null;
};

export const SplitLayoutBlock = ({ data }: { data: { left: any, right: any, split?: '50/50' | '60/40' } }) => {
    if (!data?.left && !data?.right) return null;
    return (
        <div className="flex flex-col md:flex-row gap-6 my-8">
            <div className={`flex-1 ${data.split === '60/40' ? 'md:flex-[3]' : 'md:flex-1'}`}>
                <PageContentRenderer content={data.left} />
            </div>
            <div className={`flex-1 ${data.split === '60/40' ? 'md:flex-[2]' : 'md:flex-1'}`}>
                <PageContentRenderer content={data.right} />
            </div>
        </div>
    );
};

// --- Registry ---

const BLOCK_COMPONENTS: Record<BlockType, React.FC<any>> = {
  text: TextBlock,
  metrics: MetricsBlock,
  features: FeaturesBlock,
  key_value: KeyValueBlock,
  team: TeamBlock,
  alert: AlertBlock,
  button: ButtonBlock,
  metrics_display: MetricsGrid,
  chart_stacked_bar: ChartStackedBar,
  chart_donut: ChartDonut,
  financial_table: FinancialTable,
  layout_split: SplitLayoutBlock,
  email_list: EmailList,
  metrics_grid: MetricsGrid,
  logo_grid: LogoGrid,
  comparison_table: ComparisonTable,
  process_flow: ProcessFlow,
  feature_list: FeatureList,
  timeline: Timeline,
  highlight_box: HighlightBox,
};

export const PageContentRenderer = ({ content }: { content: string | Block[] | null }) => {
    if (!content) return <div className="text-neutral-500 italic">No content available.</div>;

    let blocks: Block[] = [];

    // Parse if string
    if (typeof content === 'string') {
        try {
            // Try parsing as JSON first
            const parsed = JSON.parse(content);
            if (Array.isArray(parsed)) {
                blocks = parsed;
            } else {
                // Not an array? Treat as markdown text block
                blocks = [{ id: '1', type: 'text', data: { markdown: content } }];
            }
        } catch (e) {
            // Not JSON? Treat as markdown text block
            blocks = [{ id: '1', type: 'text', data: { markdown: content } }];
        }
    } else if (Array.isArray(content)) {
        blocks = content;
    } else {
        // If content is an object or invalid type, treat as empty or single block if needed.
        // For now, if it's not an array, we wrap it if it looks like a block, or ignore.
        // Assuming strict Block[] type usually, but just in case:
        if (content && typeof content === 'object' && 'type' in content) {
             blocks = [content as Block];
        } else {
             console.warn("PageContentRenderer: Invalid content format", content);
             return null;
        }
    }

    return (
        <div className="page-content-renderer">
            {blocks.map((block, idx) => {
                const Component = BLOCK_COMPONENTS[block.type];
                if (!Component) {
                    console.warn(`Unknown block type: ${block.type}`);
                    return null;
                }
                return <Component key={block.id || idx} data={block.data} />;
            })}
        </div>
    );
}
